import { PropertyGroupSplitBy, PropertyGroupStep } from "./types";
import { propertyGroupTitleMap } from "./constants";
import { plural } from "pluralize";

export const getNextStep = (
  currentStep: PropertyGroupStep | undefined,
  selectedSplitOptions: PropertyGroupSplitBy[]
) => {
  if (!currentStep) return PropertyGroupStep.INITIAL_CONFIGURATION;
  if (currentStep === PropertyGroupStep.INITIAL_CONFIGURATION)
    return selectedSplitOptions[0];
  const currentStepValue = currentStep.toString();
  const splitOptionValues = selectedSplitOptions.map((option) =>
    option.toString()
  );
  const currentSplitIndex = splitOptionValues.indexOf(currentStepValue);

  if (currentSplitIndex !== -1)
    if (currentSplitIndex === splitOptionValues.length - 1)
      return PropertyGroupStep.CONFIRM_GROUPS;
    else return selectedSplitOptions[currentSplitIndex + 1];

  return PropertyGroupStep.CONFIRM_GROUPS;
};

export const getPreviousStep = (
  selectedGroupByOptions: PropertyGroupSplitBy[],
  currentStep: PropertyGroupStep
) => {
  if (currentStep === PropertyGroupStep.CONFIRM_GROUPS)
    return selectedGroupByOptions[selectedGroupByOptions.length - 1];

  const index = selectedGroupByOptions.indexOf(
    currentStep as unknown as PropertyGroupSplitBy
  );
  return selectedGroupByOptions[index - 1];
};

export const getPreviousStepTitle = (
  selectedGroupByOptions: PropertyGroupSplitBy[],
  currentStep: PropertyGroupSplitBy,
  properties?: string
) => {
  if (currentStep === selectedGroupByOptions[0])
    return `all ${properties || "properties"}`;
  return plural(
    propertyGroupTitleMap[
      getPreviousStep(
        selectedGroupByOptions,
        currentStep as unknown as PropertyGroupStep
      )
    ] || ""
  );
};

const getVirtualSegments = (
  segments: any,
  split_by: PropertyGroupSplitBy[]
) => {
  if (!segments) return [];

  const leafSegments = segments.filter((segment) => !segment.has_children);

  const virtualSegments: any = [];
  leafSegments.forEach((segment: any) => {
    for (
      let currentLevel = segment.level + 1;
      currentLevel <= split_by.length;
      currentLevel++
    ) {
      const virtualId = parseInt(`${segment.id}${currentLevel}`, 10);
      const parentId =
        currentLevel === segment.level + 1
          ? segment.id
          : parseInt(`${segment.id}${currentLevel - 1}`, 10);

      const addingNotes = {
        ...segment,
        level: currentLevel,
        title: `Others`,
        id: virtualId,
        isVirtual: true,
        matched_value: [],
        parent_id: parentId
      };
      virtualSegments.push(addingNotes);
    }
  });

  return virtualSegments;
};

export const tranformData = (
  generalSegments: any,
  dictionary?: { properties: string }
) => {
  const { segments, split_by } = generalSegments;

  if (!segments)
    return {
      nodes: [],
      links: []
    };

  const virtualSegments =
    segments.length < 2 ? getVirtualSegments(segments, split_by) : [];

  const sortedSegments = segments.concat(virtualSegments).sort((a, b) => {
    if (a.level !== b.level) return a.level - b.level;
    if (a.parent_id !== b.parent_id) return a.parent_id - b.parent_id;
    return a.position - b.position;
  });

  const nodes = sortedSegments.map((segment: any) => ({
    id: segment.id,
    nodeColor: segment.level === split_by.length ? "#E6007E" : "#BEC0CA",
    title:
      segment.title === "All Properties"
        ? `All ${dictionary?.properties || "properties"}`
        : segment.title
  }));

  const links = sortedSegments
    .map((segment: any) => {
      if (!segment.parent_id) return;
      return {
        source: segment.parent_id,
        target: segment.id,
        value: segment.count
      };
    })
    .filter(Boolean);

  return { nodes, links };
};

export const generateInitialPropertyGroups = (
  splitBy: PropertyGroupSplitBy[]
) => {
  return {
    splitDataChain: ["", ...splitBy].map((split) => ({
      splitType: split,
      splitValues: [] as string[],
      isOther: true
    })),
    titleChain: ["root", ...splitBy].map(
      (split) => `Other ${plural(propertyGroupTitleMap[split] || "")}`
    ),
    identity: "",
    index: 0,
    verificationTemplateIdentifier: null,
    totalProperties: 0
  };
};

export const tranformToSankeyData = (
  segments: any,
  splitBy: PropertyGroupSplitBy[],
  dictionary?: { properties: string }
) => {
  if (!segments || !Array.isArray(segments) || segments.length === 0)
    return {
      nodes: [],
      links: []
    };

  const nodes = segments.map((segment) => ({
    id: segment.identity,
    nodeColor:
      segment.splitDataChain.length === splitBy.length + 1
        ? "#E6007E"
        : "#BEC0CA",
    title:
      segment.title === "root"
        ? `All ${dictionary?.properties || "properties"}`
        : segment.title
  }));

  const links: Array<{ source: string; target: string; value: number }> = [];
  for (let i = 1; i < segments.length; i++) {
    const currentSegment = segments[i];
    const currentIdentity = currentSegment.identity;

    const lastUnderscoreIndex = currentIdentity.lastIndexOf("_");
    if (lastUnderscoreIndex !== -1) {
      const parentIdentity = currentIdentity.substring(0, lastUnderscoreIndex);

      links.push({
        source: parentIdentity,
        target: currentIdentity,
        value: currentSegment.total_properties || 0
      });
    }
  }

  return { nodes, links };
};
